import { Popover } from "@base-ui-components/react/popover"
import { ListFilter } from "lucide-react"
import { observer } from "mobx-react"
import { twMerge } from "tailwind-merge"
import { usePortalContainer } from "../../hooks/usePortalContainer"
import { TEST_FILE_PATTERNS } from "../../utils/gitTestFileFilter"

interface TestFileDisplaySettings {
    readonly showTestFiles: boolean
    setShowTestFiles(show: boolean): void
}

export const TestFileFilter = observer(function TestFileFilter({
    settings,
    testFileCount,
    iconOnly = false,
}: {
    settings: TestFileDisplaySettings
    testFileCount: number
    iconOnly?: boolean
}) {
    const portalContainer = usePortalContainer()
    const { showTestFiles } = settings
    const modeLabel = showTestFiles ? "With tests" : "No tests"
    const stateLabel = showTestFiles ? "included" : "excluded"

    return (
        <Popover.Root>
            <Popover.Trigger
                className={twMerge(
                    "btn flex h-8 shrink-0 items-center gap-1 px-2 text-xs transition-colors hover:bg-base-300 hover:text-base-content",
                    !showTestFiles && "bg-primary/10 text-primary"
                )}
                title={`${modeLabel}: test files are ${stateLabel} across Git Log and all tasks`}
                aria-label={`Test-file filter, ${testFileCount} matching ${testFileCount === 1 ? "file" : "files"} ${stateLabel}`}
            >
                <ListFilter size={14} />
                {!iconOnly && <span>{modeLabel}</span>}
                {testFileCount > 0 && <span className="font-mono">{testFileCount}</span>}
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner side="bottom" align={iconOnly ? "end" : "start"} sideOffset={6}>
                    <Popover.Popup className="z-50 max-h-[70vh] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto border border-border bg-base-100 shadow-lg outline-none">
                        <div className="border-b border-border px-3 py-2.5">
                            <div>
                                <div className="text-sm font-medium text-base-content">Test files</div>
                                <div className="text-xs text-muted">
                                    {testFileCount === 0
                                        ? `${modeLabel} across Git Log and all tasks · no matches in this review`
                                        : `${modeLabel} across Git Log and all tasks · ${testFileCount} matching ${testFileCount === 1 ? "file" : "files"} ${stateLabel}`}
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-2 border border-border bg-base-200 p-0.5" role="group" aria-label="Test files in review">
                                <button
                                    type="button"
                                    className={twMerge(
                                        "btn h-8 px-3 text-xs transition-colors",
                                        !showTestFiles ? "bg-base-100 text-base-content shadow-sm" : "text-muted hover:bg-base-300 hover:text-base-content"
                                    )}
                                    aria-pressed={!showTestFiles}
                                    onClick={() => settings.setShowTestFiles(false)}
                                >
                                    No tests
                                </button>
                                <button
                                    type="button"
                                    className={twMerge(
                                        "btn h-8 px-3 text-xs transition-colors",
                                        showTestFiles ? "bg-base-100 text-base-content shadow-sm" : "text-muted hover:bg-base-300 hover:text-base-content"
                                    )}
                                    aria-pressed={showTestFiles}
                                    onClick={() => settings.setShowTestFiles(true)}
                                >
                                    With tests
                                </button>
                            </div>
                            <div className="mt-2 text-[11px] text-muted">
                                No tests excludes matches; With tests includes all changed files. The choice updates every Git Log review, working-copy total,
                                and input-bar summary.
                            </div>
                        </div>
                        <div className="px-3 py-2.5">
                            <div className="mb-2 text-xs font-medium text-base-content">Test patterns</div>
                            <ul className="space-y-1" aria-label="Test-file patterns">
                                {TEST_FILE_PATTERNS.map((pattern) => (
                                    <li key={pattern} className="break-all font-mono text-[11px] text-muted">
                                        {pattern}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
})
